import { useState } from "react";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { ShoppingCart, User, Calendar, CreditCard, CheckCircle, Trash2, Home, Building2, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { createBooking, updatePaymentStatus, PaymentMethod } from "@/services/data";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

type Step = "cart" | "details" | "slot" | "payment" | "success";

const STEPS: { id: Step; label: string; icon: typeof ShoppingCart }[] = [
  { id: "cart", label: "Cart", icon: ShoppingCart },
  { id: "details", label: "Details", icon: User },
  { id: "slot", label: "Schedule", icon: Calendar },
  { id: "payment", label: "Payment", icon: CreditCard },
  { id: "success", label: "Confirm", icon: CheckCircle },
];

const timeSlots = ["07:00 AM", "07:30 AM", "08:00 AM", "08:30 AM", "09:00 AM", "09:30 AM", "10:00 AM", "10:30 AM", "11:00 AM", "11:30 AM", "12:00 PM", "04:00 PM", "04:30 PM", "05:00 PM", "05:30 PM"];

export default function BookingPage() {
  const { items, removeItem, total, clearCart } = useCart();
  const { patient, isLoggedIn } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>("cart");
  const [bookingId, setBookingId] = useState("");
  const [loading, setLoading] = useState(false);

  const [collectionType, setCollectionType] = useState<"Home Collection" | "Walk-in">("Home Collection");
  const [details, setDetails] = useState({
    name: patient?.name || "",
    phone: patient?.phone || "",
    email: patient?.email || "",
    address: patient?.address || "",
    city: patient?.city || "",
    pincode: patient?.pincode || "",
  });
  const [slotDate, setSlotDate] = useState("");
  const [slotTime, setSlotTime] = useState("");
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>("UPI");
  const [upiId, setUpiId] = useState("");
  const [cardNumber, setCardNumber] = useState("");

  const stepIndex = STEPS.findIndex(s => s.id === step);

  const goNext = () => {
    const steps = STEPS.map(s => s.id);
    const idx = steps.indexOf(step);
    if (idx < steps.length - 1) setStep(steps[idx + 1]);
  };

  const handleProceedFromCart = () => {
    if (items.length === 0) { toast.error("Your cart is empty"); return; }
    if (!isLoggedIn) { toast.info("Please login to continue booking"); navigate("/login?redirect=/booking"); return; }
    setDetails({ name: patient?.name || "", phone: patient?.phone || "", email: patient?.email || "", address: patient?.address || "", city: patient?.city || "", pincode: patient?.pincode || "" });
    setStep("details");
  };

  const handleProceedFromDetails = () => {
    if (!details.name || !details.phone || !details.email) { toast.error("Please fill all required fields"); return; }
    if (collectionType === "Home Collection" && (!details.address || !details.city || !details.pincode)) {
      toast.error("Please fill address for home collection"); return;
    }
    setStep("slot");
  };

  const handleProceedFromSlot = () => {
    if (!slotDate || !slotTime) { toast.error("Please select date and time"); return; }
    setStep("payment");
  };

  const handlePayment = async () => {
    if (paymentMethod === "UPI" && !upiId) { toast.error("Please enter UPI ID"); return; }
    if ((paymentMethod === "Credit Card" || paymentMethod === "Debit Card") && cardNumber.length < 16) { toast.error("Please enter valid card number"); return; }
    setLoading(true);
    const testIds = items.filter(i => i.type === "test").map(i => i.item.id);
    const packageIds = items.filter(i => i.type === "package").map(i => i.item.id);
    const testNames = items.map(i => i.item.name);
    const booking = await createBooking({
      patientId: patient?.id || "guest",
      patientName: details.name,
      patientPhone: details.phone,
      patientEmail: details.email,
      testIds,
      packageIds,
      testNames,
      collectionType,
      address: details.address,
      city: details.city,
      pincode: details.pincode,
      slotDate,
      slotTime,
      status: "Confirmed",
      paymentStatus: "Pending",
      totalAmount: total,
    });
    await updatePaymentStatus(booking.id, "Paid", paymentMethod);
    setBookingId(booking.id);
    clearCart();
    setLoading(false);
    setStep("success");
    toast.success("Booking confirmed!");
  };

  const minDate = new Date();
  minDate.setDate(minDate.getDate() + 1);
  const minDateStr = minDate.toISOString().split("T")[0];

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <h1 className="text-2xl font-bold text-foreground mb-6">Book Your Test</h1>

      {/* Stepper */}
      {step !== "success" && (
        <div className="flex items-center mb-8 overflow-x-auto pb-2">
          {STEPS.filter(s => s.id !== "success").map((s, i) => (
            <div key={s.id} className="flex items-center">
              <div className={cn("flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full transition-colors", stepIndex > i ? "text-primary" : stepIndex === i ? "bg-primary text-primary-foreground" : "text-muted-foreground")}>
                <s.icon className="h-3.5 w-3.5" />
                <span className="hidden sm:block">{s.label}</span>
              </div>
              {i < STEPS.filter(s => s.id !== "success").length - 1 && (
                <ChevronRight className={cn("h-4 w-4 mx-1", stepIndex > i ? "text-primary" : "text-border")} />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Step: Cart */}
      {step === "cart" && (
        <div className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2 space-y-3">
            <h2 className="font-semibold text-foreground mb-4">Your Cart ({items.length} items)</h2>
            {items.length === 0 ? (
              <div className="text-center py-12 border border-dashed border-border rounded-xl text-muted-foreground">
                <ShoppingCart className="h-10 w-10 mx-auto mb-3 opacity-30" />
                <p className="font-medium mb-2">Your cart is empty</p>
                <Button variant="outline" size="sm" onClick={() => navigate("/tests")}>Browse Tests</Button>
              </div>
            ) : (
              items.map(item => (
                <div key={item.item.id} className="flex items-center justify-between p-4 border border-border rounded-xl bg-card">
                  <div>
                    <p className="font-medium text-sm text-foreground">{item.item.name}</p>
                    <Badge variant="secondary" className="text-xs mt-1">{item.type === "package" ? "Package" : "Test"}</Badge>
                  </div>
                  <div className="flex items-center gap-3">
                    <p className="font-semibold text-foreground">₹{item.item.discountedPrice}</p>
                    <button onClick={() => removeItem(item.item.id)} className="text-muted-foreground hover:text-destructive transition-colors">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="bg-card border border-border rounded-xl p-5 h-fit sticky top-24">
            <h3 className="font-semibold text-foreground mb-4">Order Summary</h3>
            <div className="space-y-2 text-sm mb-4">
              <div className="flex justify-between text-muted-foreground"><span>Subtotal</span><span>₹{total}</span></div>
              <div className="flex justify-between text-green-600"><span>Home Collection</span><span>{total >= 500 ? "Free" : "₹100"}</span></div>
              <div className="border-t border-border pt-2 flex justify-between font-bold text-foreground"><span>Total</span><span>₹{total >= 500 ? total : total + 100}</span></div>
            </div>
            <Button className="w-full" onClick={handleProceedFromCart} disabled={items.length === 0}>
              Proceed to Details
            </Button>
          </div>
        </div>
      )}

      {/* Step: Details */}
      {step === "details" && (
        <div className="max-w-xl space-y-5">
          <h2 className="font-semibold text-foreground">Patient Details</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2"><Label>Full Name *</Label><Input className="mt-1" value={details.name} onChange={e => setDetails(d => ({ ...d, name: e.target.value }))} placeholder="John Doe" /></div>
            <div><Label>Phone *</Label><Input className="mt-1" value={details.phone} onChange={e => setDetails(d => ({ ...d, phone: e.target.value }))} placeholder="9876543210" /></div>
            <div><Label>Email *</Label><Input className="mt-1" type="email" value={details.email} onChange={e => setDetails(d => ({ ...d, email: e.target.value }))} placeholder="john@email.com" /></div>
          </div>

          <div>
            <Label className="mb-3 block">Collection Type</Label>
            <div className="grid grid-cols-2 gap-3">
              {(["Home Collection", "Walk-in"] as const).map(type => (
                <button
                  key={type}
                  onClick={() => setCollectionType(type)}
                  className={cn("p-4 rounded-xl border-2 text-sm font-medium transition-all flex items-center gap-2", collectionType === type ? "border-primary bg-primary/5 text-primary" : "border-border text-muted-foreground hover:border-primary/40")}
                >
                  {type === "Home Collection" ? <Home className="h-4 w-4" /> : <Building2 className="h-4 w-4" />}
                  {type}
                </button>
              ))}
            </div>
          </div>

          {collectionType === "Home Collection" && (
            <div className="space-y-3">
              <div><Label>Address *</Label><Input className="mt-1" value={details.address} onChange={e => setDetails(d => ({ ...d, address: e.target.value }))} placeholder="House No, Street, Area" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>City *</Label><Input className="mt-1" value={details.city} onChange={e => setDetails(d => ({ ...d, city: e.target.value }))} placeholder="Bengaluru" /></div>
                <div><Label>Pincode *</Label><Input className="mt-1" value={details.pincode} onChange={e => setDetails(d => ({ ...d, pincode: e.target.value }))} placeholder="560001" /></div>
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={() => setStep("cart")}>Back</Button>
            <Button onClick={handleProceedFromDetails}>Continue to Slot</Button>
          </div>
        </div>
      )}

      {/* Step: Slot */}
      {step === "slot" && (
        <div className="max-w-xl space-y-5">
          <h2 className="font-semibold text-foreground">Choose Date & Time</h2>
          <div>
            <Label>Select Date *</Label>
            <Input type="date" className="mt-1 w-full" value={slotDate} min={minDateStr} onChange={e => setSlotDate(e.target.value)} />
          </div>
          <div>
            <Label className="mb-3 block">Select Time Slot *</Label>
            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
              {timeSlots.map(t => (
                <button
                  key={t}
                  onClick={() => setSlotTime(t)}
                  className={cn("py-2 px-1 text-xs rounded-lg border transition-all font-medium", slotTime === t ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:border-primary/40")}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={() => setStep("details")}>Back</Button>
            <Button onClick={handleProceedFromSlot}>Continue to Payment</Button>
          </div>
        </div>
      )}

      {/* Step: Payment */}
      {step === "payment" && (
        <div className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2 space-y-5">
            <h2 className="font-semibold text-foreground">Payment</h2>
            <div>
              <Label className="mb-3 block">Payment Method</Label>
              <div className="grid grid-cols-2 gap-3">
                {(["UPI", "Credit Card", "Debit Card", "Net Banking"] as PaymentMethod[]).map(m => (
                  <button
                    key={m}
                    onClick={() => setPaymentMethod(m)}
                    className={cn("p-3 rounded-xl border-2 text-sm font-medium transition-all", paymentMethod === m ? "border-primary bg-primary/5 text-primary" : "border-border text-muted-foreground hover:border-primary/40")}
                  >
                    {m === "UPI" && "📱 "}{m === "Credit Card" && "💳 "}{m === "Debit Card" && "🏧 "}{m === "Net Banking" && "🏦 "}{m}
                  </button>
                ))}
              </div>
            </div>

            {paymentMethod === "UPI" && (
              <div>
                <Label>UPI ID *</Label>
                <Input className="mt-1" placeholder="yourname@paytm / 9876543210@upi" value={upiId} onChange={e => setUpiId(e.target.value)} />
                <p className="text-xs text-muted-foreground mt-1">You'll receive a payment request on your UPI app</p>
              </div>
            )}

            {(paymentMethod === "Credit Card" || paymentMethod === "Debit Card") && (
              <div className="space-y-3">
                <div><Label>Card Number *</Label><Input className="mt-1" placeholder="1234 5678 9012 3456" maxLength={16} value={cardNumber} onChange={e => setCardNumber(e.target.value.replace(/\D/g, ""))} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Expiry</Label><Input className="mt-1" placeholder="MM/YY" /></div>
                  <div><Label>CVV</Label><Input className="mt-1" placeholder="•••" maxLength={3} type="password" /></div>
                </div>
                <div><Label>Name on Card</Label><Input className="mt-1" placeholder="As printed on card" /></div>
              </div>
            )}

            {paymentMethod === "Net Banking" && (
              <div className="p-4 bg-muted/50 rounded-xl text-sm text-muted-foreground">
                You will be redirected to your bank's secure portal to complete the payment.
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setStep("slot")}>Back</Button>
              <Button onClick={handlePayment} disabled={loading} className="min-w-32">
                {loading ? "Processing..." : `Pay ₹${total >= 500 ? total : total + 100}`}
              </Button>
            </div>
          </div>

          {/* Order summary */}
          <div className="bg-card border border-border rounded-xl p-5 h-fit">
            <h3 className="font-semibold text-foreground mb-4">Booking Summary</h3>
            <div className="space-y-2 text-sm text-muted-foreground mb-4">
              {items.map(i => (
                <div key={i.item.id} className="flex justify-between"><span className="truncate max-w-28">{i.item.name}</span><span>₹{i.item.discountedPrice}</span></div>
              ))}
              <div className="border-t border-border pt-2 flex justify-between font-bold text-foreground"><span>Total</span><span>₹{total >= 500 ? total : total + 100}</span></div>
            </div>
            <div className="text-xs text-muted-foreground space-y-1">
              <p>📅 {slotDate} at {slotTime}</p>
              <p>{collectionType === "Home Collection" ? `🏠 ${details.city}` : "🏥 Walk-in"}</p>
              <p>👤 {details.name}</p>
            </div>
          </div>
        </div>
      )}

      {/* Step: Success */}
      {step === "success" && (
        <div className="text-center py-16 max-w-md mx-auto">
          <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-10 w-10 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-foreground mb-2">Booking Confirmed!</h2>
          <p className="text-muted-foreground mb-4">Your booking ID is <strong className="text-foreground">{bookingId}</strong></p>
          <p className="text-sm text-muted-foreground mb-8">A confirmation has been sent to your email. Our phlebotomist will arrive at your scheduled time.</p>
          <div className="flex gap-3 justify-center">
            <Button variant="outline" onClick={() => navigate("/my-bookings")}>View My Bookings</Button>
            <Button onClick={() => navigate("/tests")}>Book Another Test</Button>
          </div>
        </div>
      )}
    </div>
  );
}
